import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { provideComponentStore } from '@ngrx/component-store';
import { PasLayoutSidebarHolderComponent, PasSidebarHandleService } from '@porsche-aftersales/frontend-toolbox';
import { ColDef, TextMatcherParams } from 'ag-grid-community';
import { Observable } from 'rxjs';

import { TranslatePipe } from '../../../pipes/translate.pipe';
import { criteriaDateFormatter, getDateAsFormattedString } from '../../../services/ag-grid/formatter-service';
import { UserRights } from '../../../services/user-service/user-rights.enum';
import { UserService } from '../../../services/user-service/user.service';
import { booleanToYesNoFormatter } from '../../../shared/fn/generic-formatter';
import { translate } from '../../../shared/fn/translate';
import { GenericModalContentComponent } from '../../../shared/generic-modal-content/generic-modal-content.component';
import { ColDefTemplate, fromColDefTemplates, TheGridComponent } from '../../../shared/the-grid/the-grid.component';

import { ButtonCellRendererComponent } from './button-cell-renderer.component';
import { ObjectCriteriaGroup, WarrantyCalculationMethod, YesNo } from './model';
import { RecourseCriteriaEditorComponent } from './recourse-criteria-editor.component';
import { ASTERISK, getKeyByValueIgnoreCase, RecourseCriteria, RecourseCriteriaStore } from './recourse-criteria.store';

const warrantyCalculationMethodFormatter = (params: any): string =>
  translate(params.value, `warrantyCalculationMethod.`);

const objectCriteriaGroupFormatter = (params: any): string => translate(params.value, `objectCriteriaGroup.`);

const asteriskOrValueFormatter = (params: any): string => params?.value ?? ASTERISK;

export type EnumType<T> = {
  [K in keyof T]: T[K];
};

type EnumKeyToTranslationKeyMapping<T extends Record<string, string>> = {
  [key in keyof T]: string;
};

const matchAgainstResolvedTranslationKey = <T>(
  enumeration: EnumType<T>,
  keyToTranslationKeyMap: EnumKeyToTranslationKeyMapping<any>,
  filterText: string,
  value: string,
): any => {
  const key = getKeyByValueIgnoreCase<T>(enumeration, value);
  const translation = translate(keyToTranslationKeyMap[key]);
  return translation?.toUpperCase().includes(filterText?.toUpperCase());
};

const isRecourseRelevantTextMatcher = (params: TextMatcherParams): boolean => {
  const keyToTranslationKeyMap: EnumKeyToTranslationKeyMapping<typeof YesNo> = {
    Yes: 'yesno.yes',
    No: 'yesno.no',
  };
  return matchAgainstResolvedTranslationKey<typeof YesNo>(
    YesNo,
    keyToTranslationKeyMap,
    params.filterText,
    params.value,
  );
};

const objectCriteriaGroupTextMatcher = (params: TextMatcherParams): boolean => {
  const keyToTranslationKeyMap: EnumKeyToTranslationKeyMapping<typeof ObjectCriteriaGroup> = {
    Series: 'objectCriteriaGroup.SERIES',
    SparePart: 'objectCriteriaGroup.SPARE_PART',
    WildCard: 'objectCriteriaGroup.WILD_CARD',
  };
  return matchAgainstResolvedTranslationKey<typeof ObjectCriteriaGroup>(
    ObjectCriteriaGroup,
    keyToTranslationKeyMap,
    params.filterText,
    params.value,
  );
};

const warrantyCalculationMethodTextMatcher = (params: TextMatcherParams): boolean => {
  const keyToTranslationKeyMap: EnumKeyToTranslationKeyMapping<typeof WarrantyCalculationMethod> = {
    Zp8: 'warrantyCalculationMethod.ZP8',
    CustomerDelivery: 'warrantyCalculationMethod.CUSTOMER_DELIVERY',
  };
  return matchAgainstResolvedTranslationKey<typeof WarrantyCalculationMethod>(
    WarrantyCalculationMethod,
    keyToTranslationKeyMap,
    params.filterText,
    params.value,
  );
};

@Component({
  selector: 'app-recourse-criteria',
  templateUrl: './recourse-criteria.component.html',
  styleUrls: ['./recourse-criteria.component.scss'],
  imports: [
    CommonModule,

    TheGridComponent,
    GenericModalContentComponent,
    TranslatePipe,
    RecourseCriteriaEditorComponent,
    PasLayoutSidebarHolderComponent,
  ],
  providers: [provideComponentStore(RecourseCriteriaStore)],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecourseCriteriaComponent implements AfterViewInit {
  private readonly recourseCriteriaStore = inject(RecourseCriteriaStore);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly userService = inject(UserService);

  @ViewChild('modalTemplate')
  public modalTemplate: ElementRef;

  public readonly recourseCriteria$: Observable<RecourseCriteria[]> = this.recourseCriteriaStore.recourseCriteria$;

  public readonly isFormValidAndModalNotOpenInDisplayOnlyMode$ =
    this.recourseCriteriaStore.isFormValidAndModalNotOpenInDisplayOnlyMode$;

  public readonly defaultColumnDef: ColDef<any> = DEFAULT_COL_DEF;
  public readonly columnDefs: ColDef[] = fromColDefTemplates(COL_DEF_TEMPLATES);

  public readonly canAddAndEditRecourseCriteria: boolean;

  public constructor() {
    this.canAddAndEditRecourseCriteria = this.userService.hasRight(UserRights.RECOURSE_BOOKING);
  }

  public ngAfterViewInit(): void {
    // we actually have to set it (every time). TODO: think about how to just do that once.
    this.sidebarHandleService.setVisibleInCurrentView(true);

    this.recourseCriteriaStore.patchState({ modalTemplate: this.modalTemplate });
  }

  public addRecourseCriteria(): void {
    this.recourseCriteriaStore.addRecourseCriteria();
  }

  public onConfirm(): void {
    this.recourseCriteriaStore.saveOrUpdateRecourseCriteria();
  }

  public onCancel(): void {
    this.recourseCriteriaStore.cancelRecourseCriteriaEditing();
  }
}

const DEFAULT_COL_DEF: ColDef<any> = {
  resizable: true,
  sortable: true,
  floatingFilter: true,
  unSortIcon: true,
  filter: 'agTextColumnFilter',
  minWidth: 60,
  width: 100,
};

const COL_DEF_TEMPLATES: ColDefTemplate<RecourseCriteria>[] = [
  { headerNameAndTooltipKey: 'recourseCriteria.id', typedField: 'id', sort: 'asc', width: 60 },
  {
    headerNameAndTooltipKey: 'recourseCriteria.sparePartNumberShort',
    typedField: 'sparePartNumber',
    valueFormatter: asteriskOrValueFormatter,
    width: 120,
  },
  {
    headerNameAndTooltipKey: 'recourseCriteria.supplierNumberShort',
    typedField: 'supplierNumber',
    valueFormatter: asteriskOrValueFormatter,
  },
  {
    headerNameAndTooltipKey: 'recourseCriteria.dealerNumberShort',
    typedField: 'dealerNumber',
    valueFormatter: asteriskOrValueFormatter,
  },
  {
    headerNameAndTooltipKey: 'recourseCriteria.warrantyDaysShort',
    typedField: 'warrantyDays',
    valueFormatter: asteriskOrValueFormatter,
    width: 120,
    filter: 'agTextColumnFilter',
    filterValueGetter: params => (params.data.warrantyDays == null ? '*' : params.data.warrantyDays),
  },
  {
    headerNameAndTooltipKey: 'recourseCriteria.warrantyCalculationMethodShort',
    typedField: 'warrantyCalculationMethod',
    valueFormatter: warrantyCalculationMethodFormatter,
    width: 160,
    filter: 'agTextColumnFilter',
    filterParams: {
      textMatcher: warrantyCalculationMethodTextMatcher,
    },
  },
  {
    headerNameAndTooltipKey: 'recourseCriteria.mileageShort',
    typedField: 'mileage',
    valueFormatter: asteriskOrValueFormatter,
    filter: 'agTextColumnFilter',
    filterValueGetter: params => (params.data.mileage == null ? '*' : params.data.mileage),
  },
  {
    headerNameAndTooltipKey: 'recourseCriteria.recourseRelevantShort',
    typedField: 'isRecourseRelevant',
    valueFormatter: booleanToYesNoFormatter,
    filter: 'agTextColumnFilter',
    filterParams: {
      textMatcher: isRecourseRelevantTextMatcher,
    },
  },
  {
    headerNameAndTooltipKey: 'recourseCriteria.objectCriteriaGroupShort',
    typedField: 'objectCriteriaGroup',
    valueFormatter: objectCriteriaGroupFormatter,
    width: 160,
    filter: 'agTextColumnFilter',
    filterParams: {
      textMatcher: objectCriteriaGroupTextMatcher,
    },
  },
  { headerNameAndTooltipKey: 'recourseCriteria.comment', typedField: 'comment', width: 400 },
  {
    headerNameAndTooltipKey: 'recourseCriteria.validFrom',
    typedField: 'validFrom',
    valueFormatter: criteriaDateFormatter,
    valueGetter: p => getDateAsFormattedString(p.data.validFrom),
  },
  {
    headerNameAndTooltipKey: 'recourseCriteria.validTo',
    typedField: 'validTo',
    valueFormatter: criteriaDateFormatter,
    valueGetter: p => getDateAsFormattedString(p.data.validTo),
  },
  {
    headerNameAndTooltipKey: undefined,
    headerClass: ['ag-header-last-cell'],
    cellRenderer: ButtonCellRendererComponent,
    filter: false,
    menuTabs: [],
    unSortIcon: false,
    width: 80,
  },
];
